import axios from 'axios';
import { isNil, sumBy, some } from 'lodash';

const MAX_ITEMS_IN_CART = 5;

export default {
  namespaced: true,

  state: {
    items: [],
    coupon: null,
    discount: null,
    subscribeToMailingList: true,
  },

  getters: {
    total: ({ items, discount = 0 }) =>
      sumBy(
        items,
        (item) => item.price + (item.extra.withRecoveryDetails ? 5 : 0)
      ) - discount,
    count: (state) => state.items.length,
    contains:
      ({ items }) =>
      ({ type, id }) =>
        some(items, { extra: { type, id } }),
    full: (state) => state.items.length >= MAX_ITEMS_IN_CART,
    empty: (state) => state.items.length === 0,
  },

  actions: {
    addItem({ commit, dispatch, getters }, item) {
      if (getters.full) {
        return;
      }

      commit('addItem', item);
      dispatch('updateDiscountValue');
    },

    updateItem({ commit, dispatch }, { index, item }) {
      commit('updateItem', { index, item });
      dispatch('updateDiscountValue');
    },

    removeItem({ commit, dispatch }, index) {
      commit('removeItem', index);
      dispatch('updateDiscountValue');
    },

    applyCoupon({ commit, dispatch }, coupon) {
      commit('applyCoupon', coupon);
      dispatch('updateDiscountValue');
    },

    async updateDiscountValue({ state, commit }) {
      if (!state.coupon) {
        commit('updateDiscountValue', null);
        return;
      }

      await axios
        .post('/api/checkout/price-order', {
          items: state.items.map((item) => item.extra),
          coupon: state.coupon,
        })
        .then(({ data }) => {
          const { discount } = data;

          if (isNil(discount)) {
            return;
          }

          if (discount === 0) {
            return commit('removeCoupon');
          }

          commit('updateDiscountValue', discount);
        })
        .catch((err) => {
          console.error('Cannot apply discount. ' + err);
        });
    },
  },

  mutations: {
    addItem(state, { description, price, extra = {} }) {
      state.items.push({
        description,
        price,
        extra,
      });

      // Fire "product added to cart" tracking event to GA4
      // (cf. https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#add_to_cart)
      if (window.dataLayer) {
        dataLayer.push({ ecommerce: null });
        dataLayer.push({
          event: 'add_to_cart',
          ecommerce: {
            currency: 'USD',
            value: price,
            items: [
              {
                item_id: `${extra.type}_${extra.id}`,
                item_name: description,
                currency: 'USD',
                index: 0,
                item_category: extra.type,
                price: price,
                quantity: 1,
                google_business_vertical: 'retail',
              },
            ],
            ecomm_prodid: `${extra.type}_${extra.id}`,
            ecomm_pagetype: 'basket',
            ecomm_totalvalue: price,
          },
        });
      }
    },

    updateItem(state, { index, item }) {
      state.items.splice(index, 1, item);
    },

    removeItem(state, index) {
      const removedItem = state.items[index];

      state.items.splice(index, 1);

      if (state.items.length === 0) {
        state.coupon = null;

        if (window.dataLayer) {
          dataLayer.push({
            event: 'emptycart',
          });
        }
      }

      // Fire "product removed from cart" tracking event to GA4
      // (cf. https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#remove_from_cart)
      if (window.dataLayer) {
        dataLayer.push({ ecommerce: null });
        dataLayer.push({
          event: 'remove_from_cart',
          ecommerce: {
            currency: 'USD',
            value: removedItem.price,
            items: [
              {
                item_id: `${removedItem.extra.type}_${removedItem.extra.id}`,
                item_name: removedItem.description,
                currency: 'USD',
                index: 0,
                item_category: removedItem.extra.type,
                price: removedItem.price,
                quantity: 1,
                google_business_vertical: 'retail',
              },
            ],
          },
        });
      }
    },

    applyCoupon(state, coupon) {
      state.coupon = coupon;
    },

    removeCoupon(state) {
      state.coupon = null;
      state.discount = null;
    },

    updateDiscountValue(state, discount) {
      state.discount = discount;
    },

    clear(state) {
      state.items.splice(0, state.items.length);
      state.coupon = null;
      state.discount = null;
    },

    setMailingListSubscription(state, subscribeToMailingList) {
      state.subscribeToMailingList = subscribeToMailingList;
    },
  },
};
